import { memo, useCallback, useMemo } from 'react';

import { Advice, Markdown, SectionForm, Spacer } from '@Sage/hoi-poi-ui';

import SelectHoi from 'components/SelectHoi';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { numberIfFinite } from 'utils/numbers';

function normalizeListIds(value) {
    return (Array.isArray(value) ? value : [])
        .map((id) => numberIfFinite(id))
        .filter((id) => typeof id === 'number' && id > 0);
}

const MailingListAction = memo(({ onChange, form, variables, action }) => {
    const listOptions = useMemo(
        () =>
            (variables?.lists || []).map((list) => ({
                label: list.description,
                value: list.id,
            })),
        [variables?.lists],
    );

    const sectionTitle = useMemo(() => {
        if (!form?.parameters?.entity) return;
        return `${action?.label} - ${form.parameters.entity.label}`;
    }, [action?.label, form]);

    const changeLists = useCallback(
        (value) => {
            const listIds = normalizeListIds(value);

            onChange((current) => ({
                ...current,
                parameters: {
                    ...current.parameters,
                    listIds,
                },
            }));
        },
        [onChange],
    );

    if (!form?.parameters?.entity) {
        return null;
    }

    return (
        <SectionForm title={sectionTitle} isExpandable={false}>
            {form.name === 'mailing_list_add' && (
                <>
                    <Advice
                        type="info"
                        showIcon
                        showCollapse={false}
                        title={getLiteral(
                            'label_automation_action_add_to_email_campaign_list_advise',
                        )}
                    >
                        <Markdown
                            content={getLiteral(
                                'label_automation_action_add_to_email_campaign_list_advise_desc',
                            )}
                        />
                    </Advice>
                    <Spacer y={2} />
                </>
            )}
            <SelectHoi
                label={`${getLiteral('label_list')} *`}
                placeholder={getLiteral('placeholder_select')}
                options={listOptions}
                onChange={changeLists}
                value={form.parameters?.listIds}
                isMulti
                isFullWidth
                useMenuPortal={false}
                usePlainValue
            />
        </SectionForm>
    );
});

MailingListAction.displayName = 'MailingListAction';

function mapEntityOption(entity, triggerDescription) {
    if (!entity) return undefined;

    return {
        label: getLiteralWithParameters(entity.literal, [triggerDescription]),
        literal: entity.literal,
        value: entity.value,
        iconType: entity.entity,
        entity: entity.entity,
    };
}

MailingListAction.getInitialData = (node, data) => {
    const triggerDescription = getLiteral(data?.scene?.parameters?.entity?.description);
    const listIds = normalizeListIds(node?.parameters?.listIds);

    return {
        ...node,
        parameters: {
            entity: mapEntityOption(node?.parameters?.entity, triggerDescription),
            listIds,
        },
    };
};

MailingListAction.hasEmptyFields = (form) => {
    if (!form?.parameters?.entity?.value) return true;
    return !form?.parameters?.listIds?.length;
};

MailingListAction.getSaveData = (form) => {
    const entity = form.parameters?.entity || {};

    return {
        entity: {
            value: entity.value,
            literal: entity.literal,
            entity: entity.entity,
        },
        listIds: form.parameters?.listIds || [],
    };
};

export default MailingListAction;
